import { useActor } from '@xstate/react';
import { debounce } from 'lodash';
import { FC, useCallback, useEffect } from 'react';
import { InterpreterFrom } from 'xstate';

import { Recipient } from 'api/api';

import { AddressSuggestionController } from '~src/address/delivery-address-form/components/address-suggestion-controller';
import { Spinner } from '~src/commons/components';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { useGlobalState } from '~src/global-state';
import { AnimatedButton } from '~src/ui/button';

import { useAddressFormBusinessLogic } from '~address-form/context/use-address-form';
import { DynamicAddressForm } from '~address-form/form-templates/dynamic-address-form';

import { BillingFlowMachine } from '../state-machines/billing-flow-machine';
import { RecipientFlowMachine } from '../state-machines/recipient-flow-machine';
import { ExtendedValidationHandler } from '../state-machines/shared-types';

const SIGNIFICANT_FIELDS = [
  'address_lines.0',
  'city',
  'postal_code',
  'region',
  'street',
  'street_number',
];

interface DeliveryAddressFormWithSuggestionProps {
  triggerExtendedValidation: ExtendedValidationHandler;
  formButtons?: React.ReactNode;
  fetching?: boolean;
  addressFlowService?: InterpreterFrom<RecipientFlowMachine> | InterpreterFrom<BillingFlowMachine>;
}

export const DeliveryAddressFormWithSuggestion: FC<DeliveryAddressFormWithSuggestionProps> = ({
  triggerExtendedValidation,
  formButtons,
  fetching = false,
  addressFlowService,
}) => {
  const { tICU } = useTranslations();
  const globalState = useGlobalState();

  const { addressService, formInstance, initialAddress, setExtendedAddress } =
    useAddressFormBusinessLogic();
  const [state, send] = useActor(addressService);

  const debouncedChangeHandler = useCallback(
    debounce((address: Recipient, field: string) => {
      send({ type: 'ADDRESS_CHANGED', payload: { address, field } });
    }, 300),
    [send]
  );

  const handleSubmit = (address: Recipient) => {
    setExtendedAddress(undefined);
    send({ type: 'SUBMIT', payload: address });
  };

  const isLackingAddressLineData =
    (state.context.suggestion.getSnapshot()?.matches('needs_confirmation') ||
      state.context.suggestion.getSnapshot()?.matches('ready.by_autocomplete')) &&
    state.context.streetNumber.getSnapshot()?.matches('ready.by_user_confirmation');

  const hasAddressSuggestion = state.context.suggestion
    .getSnapshot()
    ?.matches('needs_confirmation');

  const suggestionAddressAvailable =
    isLackingAddressLineData ||
    (hasAddressSuggestion && state.context.validation?.suggestion?.address);

  useEffect(() => {
    /**
     * It could happen that user filled address using autocomplete - in that
     * case we would get coordinates from Google Places API. We want to
     * remove these coordinates if user decides to manually adjust the address
     * and try to geocode that addres on backend side; for example for Sweden:
     * Industrigatan 2, 112 46 Stockholm would be manually changed to
     * Industrigatan 2, 511 62 Skene
     * Both of these addresses are valid and since we aren't validating coordinates on BE,
     * the coordinates would be wrongly assigned. That's why we're clearing them here
     */
    const subscription = formInstance.watch((_, { name, type }) => {
      if (
        name &&
        SIGNIFICANT_FIELDS.includes(name) &&
        type === 'change' &&
        state.context.streetNumber.getSnapshot()?.matches('ready')
      ) {
        formInstance.unregister('coordinates');
      }
    });
    return () => subscription.unsubscribe();
  }, [formInstance.watch]);

  useEffect(() => {
    /**
     * addressFlowService (either recipientFlowService or billingFlowService) is
     * passed only in case of inline address form
     */
    if (addressFlowService) {
      addressFlowService.onTransition(state => {
        if (state.event.type === 'CLOSE' && state.event?.data?.shouldResetForm) {
          send({ type: 'RESET_STATE' });
        }
      });
    }
  }, []);

  const getFormButtons = useCallback(() => {
    /**
     * The `formButtons` is a prop that we should be getting from fullscreen address form variants.
     * In case we're adding new address - there should be one button with "continue" action, and
     * in case we're aditing existing address - there should be component with "save" and "delete" actions.
     */

    if (formButtons) {
      return formButtons;
    } else {
      return (
        <AnimatedButton type="submit" size="normal" animKey={fetching.toString()}>
          {fetching ? (
            <Spinner size="small" />
          ) : suggestionAddressAvailable &&
            !globalState.address_form_configuration?.global_features?.enable_extended_validation ? (
            tICU('widget.delivery_address.SAVE_ANYWAY')
          ) : (
            tICU('widget.delivery_address.CONTINUE_BTN')
          )}
        </AnimatedButton>
      );
    }
  }, [formButtons, suggestionAddressAvailable, fetching]);

  return (
    <DynamicAddressForm
      address={initialAddress}
      formInstance={formInstance}
      invalidComponents={
        hasAddressSuggestion ? state.context.validation?.suggestion?.invalid_components : []
      }
      formButtons={getFormButtons()}
      onPlaceSelect={address => send({ type: 'AUTOCOMPLETE_CHANGE', payload: { address } })}
      onFormValuesChange={debouncedChangeHandler}
      formExtension={
        <AddressSuggestionController triggerExtendedValidation={triggerExtendedValidation} />
      }
      onSubmit={handleSubmit}
    />
  );
};
